import React, { useState, useEffect } from 'react';
import { ChevronLeft, Plus, Calendar, Clock, Users, Download, Settings, MapPin, Lock, User, Trash2, Mail, LogOut, Pencil } from 'lucide-react';
import { authAPI, associationsAPI, servicesAPI, reservationsAPI } from './api';

const SesizareSettings = ({ service, loading, onSave }) => {
  const [createSesizare, setCreateSesizare] = useState(!!service.createSesizare);
  const [sesizareTitle, setSesizareTitle] = useState(service.sesizareTitle || '');
  const defaultTitle = `PROGRAMARE: ${service.name}`;
  const changed = createSesizare !== !!service.createSesizare || sesizareTitle.trim() !== (service.sesizareTitle || '');

  return (
    <div className="mt-4 pt-4 border-t border-gray-200">
      <label className="flex items-center space-x-3">
        <input
          type="checkbox"
          checked={createSesizare}
          onChange={(e) => setCreateSesizare(e.target.checked)}
          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
        />
        <span className="text-gray-600">Creează o sesizare la fiecare programare</span>
      </label>
      {createSesizare && (
        <div className="mt-3 ml-7">
          <label className="block text-sm text-gray-600 mb-1">Titlu sesizare</label>
          <input
            type="text"
            value={sesizareTitle}
            onChange={(e) => setSesizareTitle(e.target.value)}
            maxLength={200}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent text-sm"
            style={{borderColor: '#971b1e', '--tw-ring-color': '#971b1e'}}
            placeholder={defaultTitle}
          />
          <p className="text-xs text-gray-500 mt-1">Dacă rămâne gol, titlul va fi „{defaultTitle}”</p>
        </div>
      )}
      {changed && (
        <div className="mt-3 flex justify-end">
          <button
            onClick={() => onSave(createSesizare, sesizareTitle)}
            disabled={loading}
            className="px-4 py-2 text-white rounded-lg text-sm disabled:opacity-50"
            style={{backgroundColor: '#971b1e'}}
          >
            Salvează
          </button>
        </div>
      )}
    </div>
  );
};

// Inline rename form; onSave(newName) returns a promise, errors are shown in place
const NameEditor = ({ value, onSave, onCancel }) => {
  const [name, setName] = useState(value);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const trimmed = name.trim();
  const valid = trimmed.length >= 3 && trimmed.length <= 100;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid || saving) return;
    if (trimmed === value) return onCancel();
    try {
      setSaving(true);
      setError('');
      await onSave(trimmed);
    } catch (err) {
      setError(err.message || 'Eroare la salvarea numelui!');
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="w-full">
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => e.key === 'Escape' && onCancel()}
        maxLength={100}
        autoFocus
        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent text-sm text-gray-900"
        style={{borderColor: '#971b1e', '--tw-ring-color': '#971b1e'}}
      />
      {!valid && <p className="text-xs text-red-600 mt-1">Numele trebuie să aibă între 3 și 100 de caractere</p>}
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
      <div className="mt-2 flex justify-end space-x-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="px-3 py-1.5 rounded-lg text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-50"
        >
          Anulează
        </button>
        <button
          type="submit"
          disabled={!valid || saving}
          className="px-3 py-1.5 text-white rounded-lg text-sm disabled:opacity-50"
          style={{backgroundColor: '#971b1e'}}
        >
          {saving ? 'Se salvează...' : 'Salvează'}
        </button>
      </div>
    </form>
  );
};

// 200 -> "200 lei", 150.5 -> "150,50 lei" (same format as the emails and PDF)
const formatCost = (cost) =>
  Number.isInteger(cost) ? `${cost} lei` : `${cost.toFixed(2).replace('.', ',')} lei`;

// Inline cost form; an empty value clears the cost. onSave(costOrNull) returns a promise
const CostEditor = ({ value, onSave, onCancel }) => {
  const [cost, setCost] = useState(value != null ? String(value) : '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const parsed = cost.trim() === '' ? null : Number(cost);
  const valid = parsed === null || (Number.isFinite(parsed) && parsed >= 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!valid || saving) return;
    try {
      setSaving(true);
      setError('');
      await onSave(parsed);
    } catch (err) {
      setError(err.message || 'Eroare la salvarea costului!');
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="w-full">
      <input
        type="number"
        min="0"
        step="0.01"
        inputMode="decimal"
        value={cost}
        onChange={(e) => setCost(e.target.value)}
        onKeyDown={(e) => e.key === 'Escape' && onCancel()}
        autoFocus
        placeholder="Gol = fără cost"
        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent text-sm text-gray-900"
        style={{borderColor: '#971b1e', '--tw-ring-color': '#971b1e'}}
      />
      {!valid && <p className="text-xs text-red-600 mt-1">Costul trebuie să fie un număr pozitiv</p>}
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
      <div className="mt-2 flex justify-end space-x-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="px-3 py-1.5 rounded-lg text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-50"
        >
          Anulează
        </button>
        <button
          type="submit"
          disabled={!valid || saving}
          className="px-3 py-1.5 text-white rounded-lg text-sm disabled:opacity-50"
          style={{backgroundColor: '#971b1e'}}
        >
          {saving ? 'Se salvează...' : 'Salvează'}
        </button>
      </div>
    </form>
  );
};

const BookingAdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginCredentials, setLoginCredentials] = useState({ username: '', password: '' });
  const [currentUser, setCurrentUser] = useState(null);
  const [loginError, setLoginError] = useState('');
  const [currentView, setCurrentView] = useState('associations');
  const [selectedAssociation, setSelectedAssociation] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [newAssociation, setNewAssociation] = useState({ name: '', address: '' });
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [editingAssociationId, setEditingAssociationId] = useState(null);
  const [editingServiceName, setEditingServiceName] = useState(false);
  const [editingServiceCost, setEditingServiceCost] = useState(false);

  // Real associations from server
  const [associations, setAssociations] = useState([]);
  
  // Real services from server
  const [services, setServices] = useState([]);
  
  // Real reservations from server
  const [reservations, setReservations] = useState([]);
  const [reservationsLoading, setReservationsLoading] = useState(false);

  const [newEvent, setNewEvent] = useState({
    name: '',
    description: '',
    startDate: '',
    endDate: '',
    startTime: '',
    endTime: '',
    excludeStartTime: '',
    excludeEndTime: '',
    duration: 30,
    interventionEmail: '',
    serviceCost: '',
    excludeWeekends: false,
    createSesizare: false,
    sesizareTitle: '',
    active: false // Default to inactive
  });

  // Check for stored tokens on component mount
  useEffect(() => {
    const checkAuthStatus = async () => {
      if (authAPI.isAuthenticated()) {
        try {
          const user = await authAPI.getCurrentUser();
          if (user) {
            setCurrentUser(user);
            setIsAuthenticated(true);
          } else {
            // Token is invalid, clear it
            await authAPI.logout();
          }
        } catch (error) {
          console.error('Error checking auth status:', error);
          await authAPI.logout();
        }
      }
    };

    checkAuthStatus();
  }, []);

  // Fetch associations from server
  const fetchAssociations = async () => {
    if (!isAuthenticated) return;
    
    setLoading(true);
    try {
      const associationsData = await associationsAPI.getAll();
      
      // Transform server data to match UI format
      const transformedAssociations = associationsData.map((assoc, index) => {
        const colors = [
          {backgroundColor: '#971b1e'}, 
          {backgroundColor: '#3d3c41'}, 
          {backgroundColor: '#7a1619'}, 
          {backgroundColor: '#2a292e'}, 
          {backgroundColor: '#971b1e'}, 
          {backgroundColor: '#3d3c41'}
        ];
        return {
          id: assoc.id,
          name: assoc.name,
          address: assoc.address,
          color: colors[index % colors.length]
        };
      });
      
      setAssociations(transformedAssociations);
    } catch (error) {
      console.error('Error fetching associations:', error);
      setErrorMessage('Eroare la încărcarea asociațiilor!');
      // If authentication fails, logout the user
      if (error.message.includes('401') || error.message.includes('403')) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch services for selected association
  const fetchServices = async (associationId) => {
    if (!isAuthenticated || !associationId) return;
    
    setServicesLoading(true);
    try {
      const servicesData = await servicesAPI.getAll(associationId);
      
      // Transform server data to match UI format and get real reservation counts
      const transformedServices = await Promise.all(servicesData.map(async (service, index) => {
        // Get real reservation count for each service
        let reservations = 0;
        try {
          const reservationsData = await servicesAPI.getReservationsWithSlots(service.id);
          reservations = reservationsData.length;
        } catch (error) {
          console.error(`Error fetching reservations for service ${service.id}:`, error);
          // Keep reservations as 0 if there's an error
        }
        
        return {
          id: service.id,
          name: service.name,
          date: service.start_date, // Use start date as the main date
          endDate: service.end_date,
          startTime: service.start_time,
          endTime: service.end_time,
          reservations: reservations,
          duration: service.slot_duration,
          description: service.description,
          serviceType: service.service_type,
          active: service.active,
          slotsGenerated: service.slots_generated,
          excludeWeekends: service.exclude_weekends,
          interventionEmail: service.intervention_email,
          serviceCost: service.service_cost ?? null,
          createSesizare: service.create_sesizare,
          sesizareTitle: service.sesizare_title || ''
        };
      }));
      
      setServices(transformedServices);
    } catch (error) {
      console.error('Error fetching services:', error);
      setErrorMessage('Eroare la încărcarea serviciilor!');
      // If authentication fails, logout the user
      if (error.message.includes('401') || error.message.includes('403')) {
        handleLogout();
      }
    } finally {
      setServicesLoading(false);
    }
  };

  // Fetch reservations for selected service
  const fetchReservations = async (serviceId) => {
    if (!isAuthenticated || !serviceId) return;
    
    setReservationsLoading(true);
    try {
      const reservationsData = await servicesAPI.getReservationsWithSlots(serviceId);
      setReservations(reservationsData);
      
      // Update the selected service with the real reservation count
      setSelectedService(prev => prev ? {
        ...prev,
        reservations: reservationsData.length
      } : null);
    } catch (error) {
      console.error('Error fetching reservations:', error);
      setErrorMessage('Eroare la încărcarea rezervărilor!');
      // If authentication fails, logout the user
      if (error.message.includes('401') || error.message.includes('403')) {
        handleLogout();
      }
    } finally {
      setReservationsLoading(false);
    }
  };

  // Load associations when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchAssociations();
    }
  }, [isAuthenticated]);

  // Login handler
  const handleLogin = async () => {
    setLoginError('');
    
    try {
      const result = await authAPI.login(loginCredentials.username, loginCredentials.password);
      setIsAuthenticated(true);
      setCurrentUser({ username: result.username });
      setLoginCredentials({ username: '', password: '' });
    } catch (error) {
      setLoginError('Nume de utilizator sau parolă incorectă!');
    }
  };

  // Logout handler
  const handleLogout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setIsAuthenticated(false);
      setCurrentUser(null);
      setAssociations([]);
      setServices([]);
      setReservations([]);
      setCurrentView('associations');
      setSelectedAssociation(null);
      setSelectedService(null);
    }
  };

  const handleCreateEvent = async () => {
    console.log('Creating service...', newEvent);
    console.log('newEvent.active value:', newEvent.active, 'type:', typeof newEvent.active);
    setErrorMessage('');
    
    if (!newEvent.name || !newEvent.startDate || !newEvent.startTime || !newEvent.endTime) {
      console.log('Missing fields:', { name: newEvent.name, startDate: newEvent.startDate, startTime: newEvent.startTime, endTime: newEvent.endTime });
      setErrorMessage('Completează toate câmpurile obligatorii!');
      return;
    }
    
    if (!selectedAssociation) {
      setErrorMessage('Asociația nu a fost selectată!');
      return;
    }
    
    try {
      // Transform the form data to match the server API format
      const serviceData = {
        association_id: selectedAssociation.id,
        name: newEvent.name,
        description: newEvent.description || '',
        service_type: newEvent.serviceType || 'Boiler Maintenance', // Use selected service type or default
        start_date: newEvent.startDate,
        end_date: newEvent.endDate || newEvent.startDate, // Use start date if end date not provided
        start_time: newEvent.startTime,
        end_time: newEvent.endTime,
        break_start_time: newEvent.excludeStartTime || null,
        break_end_time: newEvent.excludeEndTime || null,
        slot_duration: newEvent.duration,
        exclude_weekends: newEvent.excludeWeekends,
        exclude_dates: null,
        intervention_email: newEvent.interventionEmail || 'admin@example.com', // Use provided email or default
        service_cost: newEvent.serviceCost === '' ? null : parseFloat(newEvent.serviceCost),
        active: newEvent.active, // Include active status
        create_sesizare: newEvent.createSesizare,
        sesizare_title: newEvent.sesizareTitle.trim() || null
      };
      
      console.log('serviceData being sent:', serviceData);
      console.log('serviceData.active:', serviceData.active, 'type:', typeof serviceData.active);
      
      const createdService = await servicesAPI.create(serviceData);
      
      // Automatically generate slots for the new service
      try {
        await servicesAPI.generateSlots(createdService.id);
        console.log('Slots generated successfully for service:', createdService.id);
      } catch (slotError) {
        console.error('Error generating slots:', slotError);
        // Don't fail the entire operation if slot generation fails
      }
      
      // Transform the created service to match UI format with ALL fields
      const transformedService = {
        id: createdService.id,
        name: createdService.name,
        date: createdService.start_date,
        endDate: createdService.end_date, // Include end date
        startTime: createdService.start_time,
        endTime: createdService.end_time,
        reservations: 0, // New service has no reservations yet
        duration: createdService.slot_duration,
        description: createdService.description,
        serviceType: createdService.service_type,
        active: newEvent.active, // Use the chosen status from the form
        slotsGenerated: true, // Mark as generated since we just generated slots
        excludeWeekends: createdService.exclude_weekends, // Include exclude weekends
        interventionEmail: createdService.intervention_email, // Include intervention email
        serviceCost: createdService.service_cost ?? null,
        createSesizare: createdService.create_sesizare,
        sesizareTitle: createdService.sesizare_title || ''
      };
      
      console.log('Adding service:', transformedService);
      setServices(prevServices => [...prevServices, transformedService]);
      setNewEvent({
        name: '',
        description: '',
        startDate: '',
        endDate: '',
        startTime: '',
        endTime: '',
        excludeStartTime: '',
        excludeEndTime: '',
        duration: 30,
        interventionEmail: '',
        serviceCost: '',
        excludeWeekends: false,
        createSesizare: false,
        sesizareTitle: '',
        active: false // Reset to inactive
      });
      setCurrentView('events');
      setErrorMessage('');
    } catch (error) {
      console.error('Error creating service:', error);
      setErrorMessage('Eroare la crearea serviciului!');
    }
  };

  const handleCreateAssociation = async () => {
    console.log('Creating association...', newAssociation);
    setErrorMessage('');
    
    if (!newAssociation.name || !newAssociation.address) {
      console.log('Missing fields:', newAssociation);
      setErrorMessage('Completează toate câmpurile!');
      return;
    }
    
    try {
      const createdAssociation = await associationsAPI.create(newAssociation);
      
      // Transform the created association to match UI format
      const colors = [
        {backgroundColor: '#971b1e'}, 
        {backgroundColor: '#3d3c41'}, 
        {backgroundColor: '#7a1619'}, 
        {backgroundColor: '#2a292e'}, 
        {backgroundColor: '#971b1e'}, 
        {backgroundColor: '#3d3c41'}
      ];
      const transformedAssociation = {
        id: createdAssociation.id,
        name: createdAssociation.name,
        address: createdAssociation.address,
        color: colors[associations.length % colors.length]
      };
      
      console.log('Adding association:', transformedAssociation);
      setAssociations(prevAssociations => [...prevAssociations, transformedAssociation]);
      setNewAssociation({ name: '', address: '' });
      setCurrentView('associations');
      setErrorMessage('');
    } catch (error) {
      console.error('Error creating association:', error);
      setErrorMessage('Eroare la crearea asociației!');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('ro-RO', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    });
  };

  // Delete service function
  const handleDeleteService = async () => {
    if (!selectedService) return;
    
    const confirmed = window.confirm(
      `Ești sigur că vrei să ștergi serviciul "${selectedService.name}"? Această acțiune nu poate fi anulată.`
    );
    
    if (!confirmed) return;
    
    try {
      await servicesAPI.delete(selectedService.id);
      console.log('Service deleted successfully:', selectedService.id);
      
      // Remove the service from the local state
      setServices(prevServices => prevServices.filter(service => service.id !== selectedService.id));
      
      // Go back to services list
      setCurrentView('events');
      setSelectedService(null);
      setErrorMessage('');
    } catch (error) {
      console.error('Error deleting service:', error);
      setErrorMessage('Eroare la ștergerea serviciului!');
    }
  };

  // Toggle service status function
  const handleToggleServiceStatus = async () => {
    if (!selectedService) return;
    
    try {
      setLoading(true);
      setErrorMessage('');
      
      const newActiveStatus = !selectedService.active;
      
      // Update the service on the server
      await servicesAPI.update(selectedService.id, { active: newActiveStatus });
      console.log('Service status updated successfully:', selectedService.id, 'to', newActiveStatus);
      
      // Update the selected service in local state
      setSelectedService(prev => ({
        ...prev,
        active: newActiveStatus
      }));
      
      // Update the service in the services list
      setServices(prevServices => 
        prevServices.map(service => 
          service.id === selectedService.id 
            ? { ...service, active: newActiveStatus }
            : service
        )
      );
      
    } catch (error) {
      console.error('Error updating service status:', error);
      setErrorMessage('Eroare la actualizarea statusului serviciului!');
    } finally {
      setLoading(false);
    }
  };

  // Save sesizare settings for the selected service
  const handleSaveSesizareSettings = async (createSesizare, sesizareTitle) => {
    if (!selectedService) return;
    
    try {
      setLoading(true);
      setErrorMessage('');
      
      const title = sesizareTitle.trim();
      await servicesAPI.update(selectedService.id, {
        create_sesizare: createSesizare,
        sesizare_title: title || null
      });
      
      const changes = { createSesizare, sesizareTitle: title };
      setSelectedService(prev => ({ ...prev, ...changes }));
      setServices(prevServices =>
        prevServices.map(service =>
          service.id === selectedService.id ? { ...service, ...changes } : service
        )
      );
    } catch (error) {
      console.error('Error updating sesizare settings:', error);
      setErrorMessage('Eroare la salvarea setărilor pentru sesizare!');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!selectedService) return;
    
    try {
      setLoading(true);
      setErrorMessage('');
      
      console.log('Downloading PDF for service:', selectedService.id);
      await reservationsAPI.downloadPDF(selectedService.id);
      
      console.log('PDF downloaded successfully');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      setErrorMessage('Eroare la descărcarea raportului PDF!');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMail = async () => {
    if (!selectedService) return;
    
    if (!selectedService.interventionEmail) {
      setErrorMessage('Nu există email de intervenție configurat pentru acest serviciu!');
      return;
    }
    
    const confirmed = window.confirm(
      `Ești sigur că vrei să trimiți raportul PDF la adresa ${selectedService.interventionEmail}?`
    );
    
    if (!confirmed) return;
    
    try {
      setLoading(true);
      setErrorMessage('');
      
      console.log('Sending PDF report to email:', selectedService.interventionEmail);
      const result = await reservationsAPI.sendPDFEmail(selectedService.id);
      
      console.log('Email sent successfully:', result);
      alert(`Raportul PDF a fost trimis cu succes la ${selectedService.interventionEmail}`);
      
    } catch (error) {
      console.error('Error sending email:', error);
      setErrorMessage('Eroare la trimiterea email-ului! Verifică configurația SMTP.');
    } finally {
      setLoading(false);
    }
  };

  const handleRenameAssociation = async (association, name) => {
    await associationsAPI.update(association.id, { name });
    setAssociations(prev => prev.map(assoc => assoc.id === association.id ? { ...assoc, name } : assoc));
    setSelectedAssociation(prev => prev && prev.id === association.id ? { ...prev, name } : prev);
    setEditingAssociationId(null);
  };

  const handleRenameService = async (name) => {
    await servicesAPI.update(selectedService.id, { name });
    setSelectedService(prev => ({ ...prev, name }));
    setServices(prev => prev.map(service => service.id === selectedService.id ? { ...service, name } : service));
    setEditingServiceName(false);
  };

  const handleUpdateServiceCost = async (serviceCost) => {
    await servicesAPI.update(selectedService.id, { service_cost: serviceCost });
    setSelectedService(prev => ({ ...prev, serviceCost }));
    setServices(prev => prev.map(service => service.id === selectedService.id ? { ...service, serviceCost } : service));
    setEditingServiceCost(false);
  };

  const handleDeleteAssociation = async (association) => {
    const confirmed = window.confirm(
      `Ești sigur că vrei să ștergi asociația "${association.name}"?\n\n` +
      `Această acțiune va șterge:\n` +
      `• Toate serviciile asociate\n` +
      `• Toate rezervările\n` +
      `• Toate sloturile\n\n` +
      `Această acțiune nu poate fi anulată!`
    );
    
    if (!confirmed) return;
    
    try {
      setLoading(true);
      setErrorMessage('');
      
      console.log('Deleting association:', association.id);
      await associationsAPI.delete(association.id);
      
      console.log('Association deleted successfully:', association.id);
      
      // Remove the association from the local state
      setAssociations(prevAssociations => 
        prevAssociations.filter(assoc => assoc.id !== association.id)
      );
      
      // If this was the selected association, clear it
      if (selectedAssociation && selectedAssociation.id === association.id) {
        setSelectedAssociation(null);
        setServices([]);
        setSelectedService(null);
      }
      
      setErrorMessage('');
    } catch (error) {
      console.error('Error deleting association:', error);
      setErrorMessage('Eroare la ștergerea asociației!');
    } finally {
      setLoading(false);
    }
  };

  // Login View
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{backgroundColor: '#f8f9fa'}}>
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-lg shadow-lg p-8">
            {/* Logo/Title */}
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-lg flex items-center justify-center mx-auto mb-4" style={{backgroundColor: '#971b1e'}}>
                <Lock className="text-white" size={32} />
              </div>
              <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
              <p className="text-gray-500 mt-2">Conectează-te pentru a accesa panoul</p>
            </div>

            {/* Login Form */}
            <div className="space-y-4">
              {/* Error Message */}
              {loginError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-600">
                  {loginError}
                </div>
              )}

              {/* Username */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nume utilizator
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-3 text-gray-400" size={20} />
                  <input
                    type="text"
                    value={loginCredentials.username}
                    onChange={(e) => setLoginCredentials({...loginCredentials, username: e.target.value})}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="admin"
                    onKeyPress={async (e) => e.key === 'Enter' && await handleLogin()}
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Parolă
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 text-gray-400" size={20} />
                  <input
                    type="password"
                    value={loginCredentials.password}
                    onChange={(e) => setLoginCredentials({...loginCredentials, password: e.target.value})}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="admin"
                    onKeyPress={async (e) => e.key === 'Enter' && await handleLogin()}
                  />
                </div>
              </div>

              {/* Login Button */}
              <button
                onClick={handleLogin}
                disabled={!loginCredentials.username || !loginCredentials.password}
                className="w-full disabled:bg-gray-300 disabled:cursor-not-allowed text-white py-2 px-4 rounded-lg transition-colors font-medium"
                style={{
                  backgroundColor: '#971b1e',
                  ':hover': { backgroundColor: '#7a1619' }
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#7a1619'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#971b1e'}
              >
                Conectează-te
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // View: Lista asociațiilor
  if (currentView === 'associations') {
    return (
      <div className="min-h-screen" style={{backgroundColor: '#f8f9fa'}}>
        {/* Header */}
        <div className="border-b px-4 py-4 sticky top-0 z-10" style={{backgroundColor: '#3d3c41', borderColor: '#971b1e'}}>
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-bold text-white">Admin Dashboard</h1>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => {
                  console.log('Add association button clicked');
                  setCurrentView('createAssociation');
                }}
                className="text-white p-2 rounded-lg shadow-sm transition-colors"
                style={{backgroundColor: '#971b1e'}}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#7a1619'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#971b1e'}
              >
                <Plus size={20} />
              </button>
              <button
                onClick={handleLogout}
                className="text-white p-2 rounded-lg shadow-sm transition-colors"
                style={{backgroundColor: '#3d3c41'}}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#2a292e'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#3d3c41'}
                title="Deconectează-te"
              >
                <LogOut size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* Lista asociațiilor */}
        <div className="p-4 space-y-3">
          {loading ? (
            <div className="flex justify-center items-center p-8">
              <p className="text-gray-500">Se încarcă asociațiile...</p>
            </div>
          ) : associations.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-8 text-center">
              <Users size={48} className="text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Nu există asociații</h3>
              <p className="text-gray-500 mb-4">Creează prima asociație pentru a începe</p>
              <button
                onClick={() => setCurrentView('createAssociation')}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                Adaugă Asociație
              </button>
            </div>
          ) : (
            associations.map(association => (
              <div
                key={association.id}
                className="rounded-lg p-4 shadow-sm border transition-colors"
                style={{backgroundColor: 'white', borderColor: '#971b1e'}}
              >
                {editingAssociationId === association.id ? (
                  <NameEditor
                    value={association.name}
                    onSave={(name) => handleRenameAssociation(association, name)}
                    onCancel={() => setEditingAssociationId(null)}
                  />
                ) : (
                <div className="flex items-center space-x-3">
                  <div
                    className="flex-1 min-w-0 flex items-center space-x-3 cursor-pointer"
                    onClick={async () => {
                      setSelectedAssociation(association);
                      setCurrentView('events');
                      // Fetch services for this association
                      await fetchServices(association.id);
                    }}
                  >
                    <div className="w-12 h-12 shrink-0 rounded-lg flex items-center justify-center" style={association.color}>
                      <Users className="text-white" size={24} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 break-words">{association.name}</h3>
                      <div className="flex items-center text-sm text-gray-500 mt-1">
                        <MapPin size={14} className="mr-1 shrink-0" />
                        <span className="truncate">{association.address}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex shrink-0 space-x-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingAssociationId(association.id);
                      }}
                      className="bg-gray-100 hover:bg-gray-200 text-gray-700 p-2 rounded-lg shadow-sm transition-colors"
                      title="Editează numele"
                    >
                      <Pencil size={16} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteAssociation(association);
                      }}
                      className="bg-red-600 hover:bg-red-700 text-white p-2 rounded-lg shadow-sm transition-colors"
                      title="Șterge asociația"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    );
  }

  // View: Lista evenimentelor
  if (currentView === 'events') {
    return (
      <div className="min-h-screen" style={{backgroundColor: '#f8f9fa'}}>
        {/* Header */}
        <div className="border-b px-4 py-4 sticky top-0 z-10" style={{backgroundColor: '#3d3c41', borderColor: '#971b1e'}}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 min-w-0">
              <button
                onClick={() => {
                  setCurrentView('associations');
                  setServices([]);
                  setSelectedService(null);
                }}
                className="p-1 shrink-0 rounded-lg transition-colors text-white"
                style={{':hover': {backgroundColor: '#2a292e'}}}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#2a292e'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                <ChevronLeft size={20} />
              </button>
              <div className="min-w-0">
                <h1 className="text-lg font-bold text-white break-words">{selectedAssociation?.name}</h1>
                <p className="text-sm text-gray-300">Servicii</p>
              </div>
            </div>
            <button
              onClick={() => {
                console.log('Add event button clicked');
                setCurrentView('createEvent');
              }}
              className="text-white p-2 ml-3 shrink-0 rounded-lg shadow-sm transition-colors"
              style={{backgroundColor: '#971b1e'}}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#7a1619'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#971b1e'}
            >
              <Plus size={20} />
            </button>
          </div>
        </div>

        {/* Lista evenimentelor */}
        <div className="p-4 space-y-3">
          {servicesLoading ? (
            <div className="flex justify-center items-center p-8">
              <p className="text-gray-500">Se încarcă serviciile...</p>
            </div>
          ) : services.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-8 text-center">
              <Calendar size={48} className="text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Nu există servicii</h3>
              <p className="text-gray-500 mb-4">Creează primul serviciu pentru această asociație</p>
              <button
                onClick={() => setCurrentView('createEvent')}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                Adaugă Serviciu
              </button>
            </div>
          ) : (
            services.map(service => (
              <div
                key={service.id}
                onClick={async () => {
                  setSelectedService(service);
                  setCurrentView('serviceDetails');
                  // Fetch reservations for this service
                  await fetchReservations(service.id);
                }}
                className="rounded-lg p-4 shadow-sm border transition-colors cursor-pointer"
                style={{backgroundColor: 'white', borderColor: '#971b1e'}}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
              >
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-semibold text-gray-900 min-w-0 break-words">{service.name}</h3>
                  <div className="flex items-center space-x-2 ml-3 shrink-0">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      service.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {service.active ? 'Activ' : 'Inactiv'}
                    </span>
                    <span className="text-sm text-gray-500">{service.reservations} rezervări</span>
                  </div>
                </div>
                <div className="flex items-center text-sm text-gray-600 space-x-4">
                  <div className="flex items-center">
                    <Calendar size={14} className="mr-1" />
                    <span>{formatDate(service.date)}</span>
                  </div>
                  <div className="flex items-center">
                    <Clock size={14} className="mr-1" />
                    <span>{service.startTime} - {service.endTime}</span>
                  </div>
                </div>
                <div className="mt-2 text-xs text-gray-500">
                  Interval: {service.duration} min
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  }

  // View: Detalii eveniment
  if (currentView === 'serviceDetails') {
    return (
      <div className="min-h-screen" style={{backgroundColor: '#f8f9fa'}}>
        {/* Header */}
        <div className="border-b px-4 py-4 sticky top-0 z-10" style={{backgroundColor: '#3d3c41', borderColor: '#971b1e'}}>
                      <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3 min-w-0">
                <button
                  onClick={() => {
                    setEditingServiceName(false);
                    setEditingServiceCost(false);
                    setCurrentView('events');
                  }}
                  className="p-1 shrink-0 rounded-lg transition-colors text-white"
                  style={{':hover': {backgroundColor: '#2a292e'}}}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#2a292e'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                  <ChevronLeft size={20} />
                </button>
                <div className="min-w-0">
                  <h1 className="text-lg font-bold text-white break-words">{selectedService?.name}</h1>
                  <p className="text-sm text-gray-300">Rezervări</p>
                </div>
              </div>
              <div className="flex items-center space-x-2 ml-3 shrink-0">
                <button 
                  onClick={handleDownloadPDF}
                  disabled={loading}
                  className="disabled:bg-gray-400 text-white p-2 rounded-lg shadow-sm transition-colors"
                  style={{backgroundColor: '#971b1e'}}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#7a1619'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#971b1e'}
                  title="Descarcă raport PDF"
                >
                  <Download size={20} />
                </button>
                <button 
                  onClick={handleSendMail}
                  disabled={loading || !selectedService?.interventionEmail}
                  className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white p-2 rounded-lg shadow-sm transition-colors"
                  title="Trimite raport PDF prin email"
                >
                  <Mail size={20} />
                </button>
                <button 
                  onClick={handleDeleteService}
                  className="bg-red-600 hover:bg-red-700 text-white p-2 rounded-lg shadow-sm transition-colors"
                  title="Șterge serviciul"
                >
                  <Trash2 size={20} />
                </button>
              </div>
            </div>
        </div>

        {/* Informații eveniment */}
        <div className="p-4">
          {/* Error Message */}
          {errorMessage && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-600 mb-4">
              {errorMessage}
            </div>
          )}
          
          <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200 mb-4">
            <h3 className="font-semibold text-gray-900 mb-3">Detalii Serviciu</h3>
            <div className="space-y-2 text-sm">
              {/* Basic Information */}
              {editingServiceName ? (
                <div>
                  <span className="block text-gray-600 mb-1">Nume serviciu:</span>
                  <NameEditor
                    value={selectedService?.name || ''}
                    onSave={handleRenameService}
                    onCancel={() => setEditingServiceName(false)}
                  />
                </div>
              ) : (
                <div className="flex justify-between items-start">
                  <span className="text-gray-600 shrink-0">Nume serviciu:</span>
                  <div className="flex items-start ml-3 min-w-0">
                    <span className="text-gray-900 font-medium text-right break-words min-w-0">{selectedService?.name}</span>
                    <button
                      onClick={() => setEditingServiceName(true)}
                      className="ml-2 shrink-0 text-gray-500 hover:text-gray-900"
                      title="Editează numele"
                    >
                      <Pencil size={14} />
                    </button>
                  </div>
                </div>
              )}
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Status:</span>
                <div className="flex items-center space-x-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    selectedService?.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {selectedService?.active ? 'Activ' : 'Inactiv'}
                  </span>
                  <button
                    onClick={() => handleToggleServiceStatus()}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                      selectedService?.active ? 'bg-blue-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        selectedService?.active ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Perioada:</span>
                <span className="text-gray-900">{formatDate(selectedService?.date)} - {formatDate(selectedService?.endDate)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Program zilnic:</span>
                <span className="text-gray-900">{selectedService?.startTime} - {selectedService?.endTime}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Durata slot:</span>
                <span className="text-gray-900">{selectedService?.duration} minute</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Exclude weekend:</span>
                <span className="text-gray-900">{selectedService?.excludeWeekends ? 'Da' : 'Nu'}</span>
              </div>
              {editingServiceCost ? (
                <div>
                  <span className="block text-gray-600 mb-1">Cost serviciu (lei):</span>
                  <CostEditor
                    value={selectedService?.serviceCost}
                    onSave={handleUpdateServiceCost}
                    onCancel={() => setEditingServiceCost(false)}
                  />
                </div>
              ) : (
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Cost serviciu:</span>
                  <div className="flex items-center">
                    <span className="text-gray-900 font-medium">
                      {selectedService?.serviceCost != null ? formatCost(selectedService.serviceCost) : '—'}
                    </span>
                    <button
                      onClick={() => setEditingServiceCost(true)}
                      className="ml-2 shrink-0 text-gray-500 hover:text-gray-900"
                      title="Editează costul"
                    >
                      <Pencil size={14} />
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Description */}
            {selectedService?.description && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex justify-between">
                  <span className="text-gray-600">Descriere:</span>
                  <span className="text-gray-900 text-sm max-w-md">{selectedService?.description}</span>
                </div>
              </div>
            )}

            {/* Contact Information */}
            {selectedService?.interventionEmail && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex justify-between">
                  <span className="text-gray-600">Email intervenții:</span>
                  <span className="text-gray-900 text-sm">{selectedService?.interventionEmail}</span>
                </div>
              </div>
            )}

            {/* Sesizare settings */}
            {selectedService && (
              <SesizareSettings
                key={selectedService.id}
                service={selectedService}
                loading={loading}
                onSave={handleSaveSesizareSettings}
              />
            )}

            {/* Statistics */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex justify-between">
                <span className="text-gray-600">Total rezervări:</span>
                <span className="text-gray-900 font-semibold text-lg">{selectedService?.reservations}</span>
              </div>
            </div>
          </div>

          {/* Listă rezervări reale */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-4 py-3 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900">Rezervări</h3>
            </div>
            {reservationsLoading ? (
              <div className="p-4 text-center">
                <p className="text-gray-500">Se încarcă rezervările...</p>
              </div>
            ) : reservations.length === 0 ? (
              <div className="p-4 text-center">
                <p className="text-gray-500 mb-2">Nu există rezervări pentru acest serviciu</p>
                <p className="text-sm text-gray-400">Rezervările vor fi create din aplicația publică</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {reservations.map((reservation) => (
                  <div key={reservation.id} className="px-4 py-3">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-medium text-gray-900">
                          {reservation.person_data?.first_name} {reservation.person_data?.last_name}
                        </p>
                        <p className="text-sm text-gray-500">
                          Apartament {reservation.person_data?.apartment_number}
                          {reservation.person_data?.staircase && `, Scara ${reservation.person_data.staircase}`}
                        </p>
                        {reservation.slot_data && (
                          <p className="text-xs text-gray-400">
                            {new Date(reservation.slot_data.slot_date).toLocaleDateString('ro-RO')} - {reservation.slot_data.start_time}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                        <p className="text-xs text-gray-400 mt-1">
                          {reservation.person_data?.phone}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // View: Creare asociație nouă
  if (currentView === 'createAssociation') {
    return (
      <div className="min-h-screen" style={{backgroundColor: '#f8f9fa'}}>
        {/* Header */}
        <div className="border-b px-4 py-4 sticky top-0 z-10" style={{backgroundColor: '#3d3c41', borderColor: '#971b1e'}}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setCurrentView('associations')}
                className="p-1 rounded-lg transition-colors text-white"
                style={{':hover': {backgroundColor: '#2a292e'}}}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#2a292e'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                <ChevronLeft size={20} />
              </button>
              <h1 className="text-lg font-bold text-white">Asociație Nouă</h1>
            </div>
            <button
              onClick={async () => {
                console.log('Create association button clicked');
                await handleCreateAssociation();
              }}
              disabled={!newAssociation.name || !newAssociation.address}
              className="disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg transition-colors"
              style={{backgroundColor: '#971b1e'}}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#7a1619'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#971b1e'}
            >
              Creează
            </button>
          </div>
        </div>

        {/* Formular */}
        <div className="p-4 space-y-4">
          {/* Mesaj eroare */}
          {errorMessage && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-600">
              {errorMessage}
            </div>
          )}
          
          {/* Nume */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Numele asociației</label>
            <input
              type="text"
              value={newAssociation.name}
              onChange={(e) => setNewAssociation({...newAssociation, name: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent"
              style={{borderColor: '#971b1e', '--tw-ring-color': '#971b1e'}}
              placeholder="Ex: Asociația Parcul Verde"
            />
          </div>

          {/* Adresa */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Adresa</label>
            <input
              type="text"
              value={newAssociation.address}
              onChange={(e) => setNewAssociation({...newAssociation, address: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent"
              style={{borderColor: '#971b1e', '--tw-ring-color': '#971b1e'}}
              placeholder="Ex: Str. Florilor nr. 25"
            />
          </div>
        </div>
      </div>
    );
  }

  // View: Creare eveniment nou
  if (currentView === 'createEvent') {
    return (
      <div className="min-h-screen" style={{backgroundColor: '#f8f9fa'}}>
        {/* Header */}
        <div className="border-b px-4 py-4 sticky top-0 z-10" style={{backgroundColor: '#3d3c41', borderColor: '#971b1e'}}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => {
                  setCurrentView('events');
                  setReservations([]);
                  setSelectedService(null);
                }}
                className="p-1 rounded-lg transition-colors text-white"
                style={{':hover': {backgroundColor: '#2a292e'}}}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#2a292e'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                <ChevronLeft size={20} />
              </button>
              <h1 className="text-lg font-bold text-white">Serviciu Nou</h1>
            </div>
            <button
              onClick={async () => {
                console.log('Create service button clicked');
                await handleCreateEvent();
              }}
              disabled={!newEvent.name || !newEvent.startDate || !newEvent.startTime || !newEvent.endTime}
              className="disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg transition-colors"
              style={{backgroundColor: '#971b1e'}}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#7a1619'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#971b1e'}
            >
              Creează
            </button>
          </div>
        </div>

        {/* Formular */}
        <div className="p-4 space-y-4">
          {/* Mesaj eroare */}
          {errorMessage && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-600">
              {errorMessage}
            </div>
          )}
          
          {/* Denumire */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Denumire serviciu</label>
            <input
              type="text"
              value={newEvent.name}
              onChange={(e) => setNewEvent({...newEvent, name: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent"
              style={{borderColor: '#971b1e', '--tw-ring-color': '#971b1e'}}
              placeholder="Ex: Ședința lunară"
            />
          </div>

          {/* Descriere */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Descriere (opțional)</label>
            <textarea
              value={newEvent.description || ''}
              onChange={(e) => setNewEvent({...newEvent, description: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent"
              style={{borderColor: '#971b1e', '--tw-ring-color': '#971b1e'}}
              placeholder="Descrierea serviciului..."
              rows={3}
            />
          </div>

          {/* Email pentru intervenții */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email pentru intervenții</label>
            <input
              type="email"
              value={newEvent.interventionEmail}
              onChange={(e) => setNewEvent({...newEvent, interventionEmail: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent"
              style={{borderColor: '#971b1e', '--tw-ring-color': '#971b1e'}}
              placeholder="admin@example.com"
            />
          </div>

          {/* Cost serviciu */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Cost serviciu (lei) <span className="text-gray-400 font-normal">— opțional</span></label>
            <input
              type="number"
              min="0"
              step="0.01"
              inputMode="decimal"
              value={newEvent.serviceCost}
              onChange={(e) => setNewEvent({...newEvent, serviceCost: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent"
              style={{borderColor: '#971b1e', '--tw-ring-color': '#971b1e'}}
              placeholder="ex. 200"
            />
          </div>

          {/* Sesizare */}
          <div>
            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={newEvent.createSesizare}
                onChange={(e) => setNewEvent({...newEvent, createSesizare: e.target.checked})}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
              />
              <span className="text-sm font-medium text-gray-700">
                Creează o sesizare la fiecare programare
              </span>
            </label>
            <p className="text-xs text-gray-500 mt-1 ml-7">
              Pentru fiecare programare confirmată se creează o sesizare în sesizari.daytodaysupport.ro
            </p>
            {newEvent.createSesizare && (
              <div className="mt-3 ml-7">
                <label className="block text-sm font-medium text-gray-700 mb-2">Titlu sesizare</label>
                <input
                  type="text"
                  value={newEvent.sesizareTitle}
                  onChange={(e) => setNewEvent({...newEvent, sesizareTitle: e.target.value})}
                  maxLength={200}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent"
                  style={{borderColor: '#971b1e', '--tw-ring-color': '#971b1e'}}
                  placeholder={`PROGRAMARE: ${newEvent.name || '<numele serviciului>'}`}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Dacă rămâne gol, titlul va fi „PROGRAMARE: {newEvent.name || '<numele serviciului>'}”
                </p>
              </div>
            )}
          </div>

          {/* Date */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Data start</label>
              <input
                type="date"
                value={newEvent.startDate}
                onChange={(e) => setNewEvent({...newEvent, startDate: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent"
              style={{borderColor: '#971b1e', '--tw-ring-color': '#971b1e'}}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Data final</label>
              <input
                type="date"
                value={newEvent.endDate}
                onChange={(e) => setNewEvent({...newEvent, endDate: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent"
              style={{borderColor: '#971b1e', '--tw-ring-color': '#971b1e'}}
              />
            </div>
          </div>

          {/* Ore */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Ora start</label>
              <input
                type="time"
                value={newEvent.startTime}
                onChange={(e) => setNewEvent({...newEvent, startTime: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent"
              style={{borderColor: '#971b1e', '--tw-ring-color': '#971b1e'}}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Ora final</label>
              <input
                type="time"
                value={newEvent.endTime}
                onChange={(e) => setNewEvent({...newEvent, endTime: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent"
              style={{borderColor: '#971b1e', '--tw-ring-color': '#971b1e'}}
              />
            </div>
          </div>

          {/* Pauză */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Pauză (opțional)</label>
            <div className="grid grid-cols-2 gap-4">
              <input
                type="time"
                value={newEvent.excludeStartTime}
                onChange={(e) => setNewEvent({...newEvent, excludeStartTime: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent"
              style={{borderColor: '#971b1e', '--tw-ring-color': '#971b1e'}}
                placeholder="Pauză start"
              />
              <input
                type="time"
                value={newEvent.excludeEndTime}
                onChange={(e) => setNewEvent({...newEvent, excludeEndTime: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent"
              style={{borderColor: '#971b1e', '--tw-ring-color': '#971b1e'}}
                placeholder="Pauză final"
              />
            </div>
          </div>

          {/* Durata */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Durata unei rezervări</label>
            <select
              value={newEvent.duration}
              onChange={(e) => setNewEvent({...newEvent, duration: parseInt(e.target.value)})}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent"
              style={{borderColor: '#971b1e', '--tw-ring-color': '#971b1e'}}
            >
              <option value={15}>15 minute</option>
              <option value={30}>30 minute</option>
              <option value={45}>45 minute</option>
              <option value={60}>60 minute</option>
              <option value={90}>90 minute</option>
              <option value={120}>120 minute</option>
            </select>
          </div>

          {/* Exclude Weekends */}
          <div>
            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={newEvent.excludeWeekends}
                onChange={(e) => setNewEvent({...newEvent, excludeWeekends: e.target.checked})}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
              />
              <span className="text-sm font-medium text-gray-700">
                Exclude weekendurile (sâmbătă și duminică)
              </span>
            </label>
            <p className="text-xs text-gray-500 mt-1 ml-7">
              Dacă este bifat, sloturile nu vor fi generate pentru weekenduri
            </p>
          </div>

          {/* Service Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status serviciu</label>
            <div className="flex items-center space-x-3">
              <span className={`text-sm font-medium ${!newEvent.active ? 'text-gray-900' : 'text-gray-500'}`}>
                Inactiv
              </span>
              <button
                type="button"
                onClick={() => setNewEvent({...newEvent, active: !newEvent.active})}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  newEvent.active ? 'bg-blue-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    newEvent.active ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className={`text-sm font-medium ${newEvent.active ? 'text-gray-900' : 'text-gray-500'}`}>
                Activ
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Serviciile inactive nu vor fi vizibile în aplicația publică
            </p>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default BookingAdminDashboard;
